import client from 'client'
import type {
  UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutMutationRequest,
  UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutMutationResponse,
  UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutPathParams,
  UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutQueryParams,
  UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPut422,
} from '../../types/UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPut.ts'
import type { UseMutationOptions } from '@tanstack/react-query'
import type { RequestConfig, ResponseConfig, ResponseErrorConfig } from 'client'
import { useMutation } from '@tanstack/react-query'

export const updateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutMutationKey = () =>
  [{ url: '/wireless_survey/{survey_id}/data_collector/{data_collector_id}' }] as const

export type UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutMutationKey = ReturnType<
  typeof updateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutMutationKey
>

/**
 * @summary Update Wireless Survey Data Collector
 * {@link /wireless_survey/:survey_id/data_collector/:data_collector_id}
 */
export async function updateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPut(
  survey_id: UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutPathParams['survey_id'],
  data_collector_id: UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutPathParams['data_collector_id'],
  data: UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutMutationRequest,
  params?: UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutQueryParams,
  config: Partial<RequestConfig<UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutMutationRequest>> & {
    client?: typeof client
  } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<
    UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutMutationResponse,
    ResponseErrorConfig<UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPut422>,
    UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutMutationRequest
  >({ method: 'PUT', url: `/wireless_survey/${survey_id}/data_collector/${data_collector_id}`, params, data, ...requestConfig })
  return res
}

/**
 * @summary Update Wireless Survey Data Collector
 * {@link /wireless_survey/:survey_id/data_collector/:data_collector_id}
 */
export function useUpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPut(
  options: {
    mutation?: UseMutationOptions<
      ResponseConfig<UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutMutationResponse>,
      ResponseErrorConfig<UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPut422>,
      {
        survey_id: UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutPathParams['survey_id']
        data_collector_id: UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutPathParams['data_collector_id']
        data: UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutMutationRequest
        params?: UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutQueryParams
      }
    >
    client?: Partial<RequestConfig<UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutMutationRequest>> & {
      client?: typeof client
    }
  } = {},
) {
  const { mutation: mutationOptions, client: config = {} } = options ?? {}
  const mutationKey = mutationOptions?.mutationKey ?? updateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutMutationKey()

  return useMutation<
    ResponseConfig<UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutMutationResponse>,
    ResponseErrorConfig<UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPut422>,
    {
      survey_id: UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutPathParams['survey_id']
      data_collector_id: UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutPathParams['data_collector_id']
      data: UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutMutationRequest
      params?: UpdateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPutQueryParams
    }
  >({
    mutationFn: async ({ survey_id, data_collector_id, data, params }) => {
      return updateWirelessSurveyDataCollectorWirelessSurveySurveyIdDataCollectorDataCollectorIdPut(survey_id, data_collector_id, data, params, config)
    },
    mutationKey,
    ...mutationOptions,
  })
}